import * as Phaser from 'phaser'
import { cameraLerp } from '../engine/camera'
import { sortKey } from '../engine/depthSort'
import { InputSystem } from '../input/InputSystem'
import { KeyboardSource } from '../input/KeyboardSource'
import { TapToWalkSource } from '../input/TapToWalkSource'
import { Player } from '../entities/Player'
import { NPC } from '../entities/NPC'

/**
 * WorldScene — the Phase-1 convergence scene. Loads the greybox tilemap, spawns
 * Player + two NPCs (the permanent depth-sort canary), wires InputSystem with
 * Keyboard + TapToWalk sources, and follows the player with prm-aware camera lerp.
 *
 * Plan 01-06 (shell boot) constructs the Phaser.Game and, on touch devices,
 * attaches a JoystickSource via `this.inputSystem.register(...)`. `inputSystem`
 * is therefore PUBLIC from the start — not mutated cross-plan.
 */
export class WorldScene extends Phaser.Scene {
  public inputSystem!: InputSystem
  private player!: Player
  private npcs: NPC[] = []
  private sortables: (Player | NPC)[] = []
  private tapSource!: TapToWalkSource
  private spawnX = 0
  private spawnY = 0
  private obstaclesLayer!: Phaser.Tilemaps.TilemapLayer

  constructor() {
    super('world')
  }

  preload(): void {
    this.load.tilemapTiledJSON('greybox', '/tilemaps/greybox.tmj')
    this.load.image('greybox-tiles', '/tilesets/greybox-tiles.png')
    this.load.atlas('player', '/sprites/player.png', '/sprites/player.json')
    this.load.atlas('npc-a', '/sprites/npc-a.png', '/sprites/npc-a.json')
    this.load.atlas('npc-b', '/sprites/npc-b.png', '/sprites/npc-b.json')
  }

  create(): void {
    // ── Tilemap ──
    const map = this.make.tilemap({ key: 'greybox' })
    const tileset = map.addTilesetImage('greybox-tiles', 'greybox-tiles')!
    map.createLayer('ground', tileset, 0, 0)!
    this.obstaclesLayer = map.createLayer(
      'obstacles',
      tileset,
      0,
      0,
    ) as Phaser.Tilemaps.TilemapLayer
    this.obstaclesLayer.setCollisionByProperty({ collides: true })

    // ── Spawn ──
    const spawnObj = map.findObject('spawn', (o) => o.name === 'player-spawn')
    this.spawnX = spawnObj?.x ?? 320
    this.spawnY = spawnObj?.y ?? 320

    // ── Animations (atlas-prefixed keys — zero cross-atlas collision) ──
    this.createAnimations('player')
    this.createAnimations('npc-a')
    this.createAnimations('npc-b')

    // ── Player ──
    this.player = new Player(this, this.spawnX, this.spawnY)
    this.physics.add.collider(this.player, this.obstaclesLayer, () => {
      // Tap-to-walk must halt on collision (CONTEXT.md: "Stop on collision").
      this.tapSource?.cancel()
    })

    // ── NPCs from tilemap npc-paths object layer ──
    const pathA = this.getPolyline(map, 'npc-a-path')
    const pathB = this.getPolyline(map, 'npc-b-path')
    this.npcs.push(new NPC(this, 'npc-a', pathA))
    this.npcs.push(new NPC(this, 'npc-b', pathB))
    this.sortables = [this.player, ...this.npcs]

    // ── Input ──
    this.inputSystem = new InputSystem()
    this.inputSystem.register(new KeyboardSource(this))
    this.tapSource = new TapToWalkSource(this, () => ({ x: this.player.x, y: this.player.y }))
    this.inputSystem.register(this.tapSource)

    // ── Camera with prm-aware lerp (ENGN-05) ──
    const prm = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const lerp = cameraLerp(prm)
    this.cameras.main.startFollow(this.player, true, lerp.x, lerp.y)
    this.cameras.main.roundPixels = true // ENGN-04
  }

  override update(_time: number, dt: number): void {
    this.inputSystem.tick()
    if (this.inputSystem.consumeReset()) {
      this.player.respawn(this.spawnX, this.spawnY)
      this.tapSource.cancel()
    }
    this.player.applyVector(this.inputSystem.vector)
    for (const npc of this.npcs) npc.tick(dt)
    this.depthSortAll()
  }

  private depthSortAll(): void {
    for (const s of this.sortables) {
      s.setDepth(sortKey(s.tileX, s.tileY, s.y))
    }
  }

  private createAnimations(atlasKey: string): void {
    const dirs = ['n', 'e', 's', 'w'] as const
    for (const d of dirs) {
      const walkKey = `${atlasKey}-walk-${d}`
      const idleKey = `${atlasKey}-idle-${d}`
      if (!this.anims.exists(walkKey)) {
        this.anims.create({
          key: walkKey,
          frames: [
            { key: atlasKey, frame: `walk-${d}-0` },
            { key: atlasKey, frame: `walk-${d}-1` },
          ],
          frameRate: 6,
          repeat: -1,
        })
      }
      if (!this.anims.exists(idleKey)) {
        this.anims.create({
          key: idleKey,
          frames: [{ key: atlasKey, frame: `idle-${d}` }],
          frameRate: 1,
          repeat: -1,
        })
      }
    }
  }

  private getPolyline(map: Phaser.Tilemaps.Tilemap, name: string): { x: number; y: number }[] {
    const obj = map.findObject(
      'npc-paths',
      (o) => o.name === name,
    ) as Phaser.Types.Tilemaps.TiledObject | null
    if (!obj?.polyline) throw new Error(`NPC polyline "${name}" missing in tilemap`)
    return obj.polyline.map((p) => ({ x: (obj.x ?? 0) + p.x, y: (obj.y ?? 0) + p.y }))
  }
}
